import { DriverV2Output, VehicleOutput } from '@allocab/allocab-driver-service-types';
import { CircleCheckSolid } from '@allocab/allocab-icons';
import { getPanelNameLabelByKey } from '@allocab/allocab-shared-utils';
import { useParams, useSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';

import { Link } from '../../components/atomic/Link';
import { BadgeIndicator } from '../../components/BadgeIndicator';
import { Spinner } from '../../components/Spinner';
import { Table } from '../../components/table/Table';
import { TableColumn } from '../../components/table/types';
import { VehicleAssignButton } from '../../components/vehicle-assign-button/VehicleAssignButton';
import { useFetchDriver } from '../../hooks/useFetchDriver';
import { useFethVehiclesForDriverPage, VEHICLES_LIMIT } from '../../hooks/useFetchVehiclesForDriver';
import { t } from '../../services/translation/translation';
import { formatDate, parseDate } from '../../utils/date';
import { getEngineTypeText } from '../../utils/vehicle';
import { VehicleOptionsIndicator } from '../_authenticated._main.vehicles/VehicleOptionsIndicator';

const columns: TableColumn<VehicleOutput, DriverV2Output>[] = [
  {
    name: t('common.licence-plate'),
    render: vehicle => <Link to={`/vehicles/${vehicle.id}`}>{vehicle.licensePlate ?? '--'}</Link>,
    sortKey: 'licensePlate',
  },
  {
    name: t('common.status'),
    render: vehicle => (
      <div className="w-32">
        <BadgeIndicator variant={vehicle.validated ? 'green' : 'yellow'}>
          {vehicle.validated ? t('common.validated') : t('common.validating')}
        </BadgeIndicator>
      </div>
    ),
    sortKey: 'validated',
  },
  {
    name: t('common.range'),
    render: vehicle => (
      <span className="capitalize">{`${vehicle.brand?.toLowerCase() ?? ''} ${
        vehicle.range?.toLowerCase() ?? ''
      }`}</span>
    ),
    sortKey: 'brand',
  },
  {
    name: t('common.color'),
    render: vehicle => <span className="capitalize">{vehicle.color?.toLowerCase() ?? '--'}</span>,
    sortKey: 'color',
  },
  {
    name: t('common.panel'),
    render: vehicle => vehicle.panelKeys?.map(p => getPanelNameLabelByKey(p)).join(',') ?? '--',
  },
  {
    name: t('common.first-date'),
    render: vehicle =>
      vehicle.firstDate ? formatDate(parseDate(vehicle.firstDate, 'DD-MM-YYYY'), t('date.date-short')) : '--',
    sortKey: 'firstDate',
  },
  {
    name: t('common.engine-type'),
    render: vehicle => (vehicle.engineType ? getEngineTypeText(vehicle.engineType) : '--'),
    sortKey: 'engineType',
  },
  {
    name: t('common.options'),
    render: vehicle => <VehicleOptionsIndicator vehicle={vehicle} />,
  },
  {
    name: t('common.current-vehicle'),
    render: vehicle =>
      vehicle.current ? (
        <CircleCheckSolid width={16} height={16} className="fill-green-700 dark:fill-green-600" />
      ) : null,
    sortKey: 'current',
  },
  {
    name: t('common.assign'),
    render: (vehicle, context) =>
      !vehicle.current && vehicle.validated ? <VehicleAssignButton drivers={[context]} vehicleId={vehicle.id} /> : null,
  },
];

export const DriverVehiclesTable = () => {
  const { driverKey } = useParams();

  invariant(driverKey);

  const { data: driver, isLoading } = useFetchDriver(driverKey);

  const [searchParams] = useSearchParams();
  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);
  const orderBy = searchParams.get('orderBy') ?? undefined;
  const direction = (searchParams.get('direction') ?? undefined) as 'desc' | 'asc' | undefined;
  const { data: vehicles, isLoading: isLoadingDrivers } = useFethVehiclesForDriverPage(
    driverKey,
    { direction, orderBy },
    currentPage,
  );
  const nbPages = Math.ceil((vehicles?.totalItems ?? 0) / VEHICLES_LIMIT);

  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!driver) {
    return (
      <div>
        <h3 className="text-3xl font-bold dark:text-white mb-6">{t('common.error')}</h3>
        <div className="inline-block space-y-2">
          <div className="h-96 w-96 bg-confused-light dark:bg-confused-dark bg-no-repeat bg-contain bg-center" />
          <p className="text-center">{t('driver-route.not-found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Table
        identifier="id"
        title={t('driver-route.vehicle-list-title')}
        caption={t('driver-route.vehicle-list-description')}
        columns={columns}
        data={vehicles?.items}
        isLoading={isLoadingDrivers}
        context={driver}
        totalItems={vehicles?.totalItems ?? 0}
        nbPages={nbPages}
      />
    </div>
  );
};
